import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { useFiliera } from "@/context/FilieraContext";
import { PageHeader, Btn, Field, Select, Badge } from "@/components/UI";
import { UploadSimple, Robot, CheckCircle, FilePdf, FileImage, FileXls, FileDoc, FileArchive, File as FileIcon, Sparkle, Eye, FloppyDisk, TreeStructure } from "@phosphor-icons/react";
import FilePreview from "@/components/FilePreview";

function iconFor(ext) {
  const m = { pdf: FilePdf, png: FileImage, jpg: FileImage, jpeg: FileImage,
    xlsx: FileXls, xls: FileXls, csv: FileXls, docx: FileDoc, doc: FileDoc, zip: FileArchive };
  return m[ext] || FileIcon;
}

export default function UploadAI() {
  const { selectedFilieraId, selectedFiliera } = useFiliera();
  const [beneficiari, setBeneficiari] = useState([]);
  const [investimenti, setInvestimenti] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [ctx, setCtx] = useState({ filiera_id: selectedFilieraId || "", beneficiario_id: "", investimento_id: "", fornitore_id: "" });
  const [files, setFiles] = useState([]);
  const [model, setModel] = useState("gemini");
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState({}); // { fileId: analysisObj }
  const [preview, setPreview] = useState(null);
  const [lastUpload, setLastUpload] = useState(null); // ephemeral UI-only confirmation
  const inputRef = useRef();

  const statusOf = (f) => {
    if (f._analyzing) return { label: "In analisi", tone: "blue" };
    if (f.ai_analysis?.error) return { label: "Errore", tone: "red" };
    if (f.ai_analysis) return { label: "Analizzato", tone: "green" };
    if (f.classificato) return { label: "Classificato", tone: "green" };
    return { label: "Caricato", tone: "slate" };
  };

  useEffect(() => {
    api.get("/beneficiari").then((r) => setBeneficiari(r.data));
    api.get("/investimenti").then((r) => setInvestimenti(r.data));
    api.get("/fornitori").then((r) => setFornitori(r.data));
  }, []);

  useEffect(() => {
    setCtx((prev) => ({
      ...prev,
      filiera_id: selectedFilieraId || "",
      beneficiario_id: "",
      investimento_id: "",
    }));
    setEditing({});
    setPreview(null);
    setLastUpload(null);
  }, [selectedFilieraId]);

  const loadFiles = async () => {
    if (!selectedFilieraId) {
      setFiles([]);
      return;
    }
    const { data } = await api.get("/files", { params: { filiera_id: selectedFilieraId } });
    setFiles(data);
  };

  useEffect(() => {
    loadFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilieraId]);

  const handleUpload = async (fileList) => {
    if (!selectedFilieraId) { alert("Seleziona una filiera attiva prima di caricare"); return; }
    setUploading(true);
    const uploadedNames = Array.from(fileList).map((f) => f.name);
    for (const f of Array.from(fileList)) {
      const fd = new FormData();
      fd.append("file", f);
      Object.entries({ ...ctx, filiera_id: selectedFilieraId }).forEach(([k, v]) => v && fd.append(k, v));
      try {
        await api.post("/files/upload", fd);
      } catch (e) { console.error(e); }
    }
    setUploading(false);
    if (uploadedNames.length > 0) {
      const stamp = Date.now();
      setLastUpload({ names: uploadedNames, ts: stamp });
      setTimeout(() => setLastUpload((cur) => (cur && cur.ts === stamp ? null : cur)), 6000);
    }
    loadFiles();
  };

  const analyze = async (fileId) => {
    setFiles((prev) => prev.map((f) => f.id === fileId ? { ...f, _analyzing: true } : f));
    try {
      const { data } = await api.post("/ai/analyze", { file_id: fileId, model });
      setFiles((prev) => prev.map((f) => f.id === fileId ? { ...f, ai_analysis: data.analysis, classificato: true, _analyzing: false } : f));
    } catch (e) {
      alert("Errore analisi AI: " + (e.response?.data?.detail || e.message));
      setFiles((prev) => prev.map((f) => f.id === fileId ? { ...f, _analyzing: false } : f));
    }
  };

  const applyToDocument = async (file) => {
    if (!selectedFilieraId) { alert("Seleziona una filiera attiva"); return; }
    try {
      await api.post("/ai/apply", {
        file_id: file.id, filiera_id: selectedFilieraId,
        beneficiario_id: ctx.beneficiario_id, investimento_id: ctx.investimento_id,
      });
      alert("Documento creato in anagrafica!");
      loadFiles();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  if (!selectedFilieraId) {
    return (
      <div className="fade-in" data-testid="upload-page">
        <PageHeader eyebrow="Document Center" title="Upload & AI OCR" />
        <div className="p-8">
          <div className="bg-white border border-slate-200 rounded-md p-10 text-center" data-testid="upload-neutral-state">
            <TreeStructure size={36} weight="duotone" className="mx-auto text-slate-400" />
            <h2 className="mt-4 text-lg font-semibold text-slate-900">Seleziona una filiera prima del caricamento</h2>
            <p className="mt-2 text-sm text-slate-500 max-w-xl mx-auto">
              Usa il selettore “Filiera attiva” nella barra laterale. I documenti caricati e le analisi AI saranno così associati al progetto corretto.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in" data-testid="upload-page">
      <PageHeader eyebrow="Document Center" title="Upload & AI OCR" />
      <div className="p-8 space-y-6">
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="eyebrow mb-3">Contesto caricamento</div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Field label="Filiera attiva">
              <div className="h-10 rounded-md border border-slate-200 bg-slate-50 px-3 flex items-center text-sm font-medium text-slate-700" data-testid="upload-active-filiera">
                {selectedFiliera?.nome || selectedFilieraId}
              </div>
            </Field>
            <Field label="Beneficiario">
              <Select value={ctx.beneficiario_id} onChange={(e) => setCtx({ ...ctx, beneficiario_id: e.target.value, investimento_id: "" })} data-testid="upload-beneficiario">
                <option value="">--</option>
                {beneficiari.filter((b) => b.filiera_id === selectedFilieraId).map((b) => <option key={b.id} value={b.id}>{b.ragione_sociale}</option>)}
              </Select>
            </Field>
            <Field label="Investimento">
              <Select value={ctx.investimento_id} onChange={(e) => setCtx({ ...ctx, investimento_id: e.target.value })} data-testid="upload-investimento">
                <option value="">--</option>
                {investimenti.filter((i) => (!ctx.beneficiario_id || i.beneficiario_id === ctx.beneficiario_id) && (!i.filiera_id || i.filiera_id === selectedFilieraId)).map((i) => <option key={i.id} value={i.id}>{i.codice}</option>)}
              </Select>
            </Field>
            <Field label="Fornitore">
              <Select value={ctx.fornitore_id} onChange={(e) => setCtx({ ...ctx, fornitore_id: e.target.value })} data-testid="upload-fornitore">
                <option value="">--</option>
                {fornitori.map((f) => <option key={f.id} value={f.id}>{f.ragione_sociale}</option>)}
              </Select>
            </Field>
            <Field label="Modello AI">
              <Select value={model} onChange={(e) => setModel(e.target.value)} data-testid="upload-model">
                <option value="gemini">Gemini 3 Flash</option>
                <option value="openai">GPT-5.2</option>
                <option value="anthropic">Claude Sonnet 4.5</option>
              </Select>
            </Field>
          </div>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-blue-500", "bg-blue-50"); }}
          onDragLeave={(e) => e.currentTarget.classList.remove("border-blue-500", "bg-blue-50")}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.classList.remove("border-blue-500", "bg-blue-50");
            handleUpload(e.dataTransfer.files);
          }}
          className="border-2 border-dashed border-slate-300 rounded-md p-12 text-center bg-white transition-colors cursor-pointer"
          onClick={() => inputRef.current?.click()} data-testid="upload-dropzone">
          <UploadSimple size={40} className="mx-auto text-slate-400" weight="duotone" />
          <div className="mt-3 text-slate-700 font-medium">Trascina qui i file o clicca per selezionarli</div>
          <div className="text-xs text-slate-500 mt-1">PDF, immagini, Excel, CSV, Word, ZIP</div>
          <input ref={inputRef} type="file" multiple hidden onChange={(e) => handleUpload(e.target.files)}
            data-testid="upload-input" />
          {uploading && <div className="mt-4 text-blue-600 text-sm animate-pulse">Caricamento in corso…</div>}
        </div>

        {lastUpload && (
          <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-md fade-in" data-testid="upload-confirmation">
            <CheckCircle size={20} weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-emerald-900">
                {lastUpload.names.length === 1
                  ? "Documento caricato con successo"
                  : `${lastUpload.names.length} documenti caricati con successo`}
              </div>
              <div className="text-xs text-emerald-700 mt-0.5 truncate">
                {lastUpload.names.join(" · ")} — pronti per l&apos;analisi AI
              </div>
            </div>
            <button onClick={() => setLastUpload(null)}
              className="text-emerald-700 hover:text-emerald-900 text-xs font-medium" data-testid="upload-confirmation-dismiss">
              Chiudi
            </button>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-md">
          <div className="p-5 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="eyebrow">Documenti caricati</div>
              <div className="text-sm text-slate-500 mt-1">{files.length} file · {files.filter((f) => f.classificato).length} classificati</div>
            </div>
          </div>
          <div className="divide-y divide-slate-200">
            {files.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">Nessun file caricato</div>}
            {files.map((f) => {
              const Icon = iconFor(f.ext);
              const st = statusOf(f);
              return (
                <div key={f.id} className="p-5 flex items-start gap-4" data-testid={`file-${f.id}`}>
                  <div className="w-11 h-11 rounded bg-slate-100 flex items-center justify-center text-slate-600 flex-shrink-0">
                    <Icon size={22} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-base text-slate-900 truncate" title={f.filename} data-testid={`file-name-${f.id}`}>{f.filename}</div>
                      <Badge tone={st.tone} data-testid={`file-status-${f.id}`}>{st.label}</Badge>
                    </div>
                    <div className="mt-1 text-xs text-slate-500 flex items-center gap-2">
                      <span className="uppercase mono">{f.ext}</span>
                      <span aria-hidden>·</span>
                      <span>{(f.size / 1024).toFixed(1)} KB</span>
                    </div>
                    {f.ai_analysis && (
                      <div className="mt-3 p-3 bg-slate-50 rounded-md text-xs space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 font-semibold text-slate-700">
                            <Sparkle size={12} weight="fill" /> Analisi AI · Modifica campi prima di registrare
                          </div>
                          {editing[f.id] && (
                            <button className="text-xs text-blue-700 hover:underline flex items-center gap-1"
                              onClick={async () => {
                                await api.put(`/files/${f.id}/analysis`, editing[f.id]);
                                setFiles((prev) => prev.map((x) => x.id === f.id ? { ...x, ai_analysis: editing[f.id] } : x));
                                setEditing((prev) => { const p = { ...prev }; delete p[f.id]; return p; });
                              }} data-testid={`save-analysis-${f.id}`}>
                              <FloppyDisk size={13} weight="bold" /> Salva modifiche
                            </button>
                          )}
                        </div>
                        {f.ai_analysis.error ? (
                          <div className="text-red-600">{f.ai_analysis.error}</div>
                        ) : (
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mt-1">
                            {["tipo", "numero", "data", "fornitore_ragione_sociale", "fornitore_piva", "oggetto", "imponibile", "iva", "totale", "cup", "cig", "dicitura_pnrr", "modalita_pagamento", "cro_trn"].map((k) => {
                              const cur = editing[f.id]?.[k] ?? f.ai_analysis[k] ?? "";
                              return (
                                <label key={k} className="block">
                                  <span className="text-slate-400 block mb-0.5">{k}</span>
                                  <input value={cur ?? ""} onChange={(e) => setEditing((prev) => ({
                                    ...prev, [f.id]: { ...(prev[f.id] || f.ai_analysis), [k]: e.target.value }
                                  }))}
                                    className="w-full h-7 px-2 rounded border border-slate-200 text-xs bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none"
                                    data-testid={`edit-${f.id}-${k}`} />
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setPreview({ id: f.id, filename: f.filename, ext: f.ext, contentType: f.content_type })}
                      className="w-9 h-9 rounded flex items-center justify-center hover:bg-slate-100 text-slate-600"
                      title="Anteprima" data-testid={`preview-${f.id}`}>
                      <Eye size={16} />
                    </button>
                    <Btn variant="secondary" onClick={() => analyze(f.id)} disabled={f._analyzing}
                      data-testid={`analyze-${f.id}`}>
                      <Robot size={16} weight="duotone" />
                      {f._analyzing ? "Analisi..." : "Analizza"}
                    </Btn>
                    {f.ai_analysis && !f.ai_analysis.error && (
                      <Btn variant="accent" onClick={() => applyToDocument(f)}
                        className="ring-2 ring-blue-200 shadow-sm"
                        data-testid={`apply-${f.id}`}>
                        <CheckCircle size={16} weight="fill" /> Registra risultato
                      </Btn>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {preview && <FilePreview {...preview} fileId={preview.id} onClose={() => setPreview(null)} />}
    </div>
  );
}
